/**
 * FighterDroneLaser.ts
 * Pooled left-firing laser bolt for the World 2 `FighterDrone`.
 *
 * Sibling to `BossProjectile` — same pooled-entity shape and pool
 * contract, identical kinematics (always travels LEFT, despawns on
 * left-edge cull). Lives in its own file so the FighterDrone weapon
 * pool stays isolated from the boss turret pool.
 *
 * Pool contract: implements `reset()` for `ObjectPool` recycling.
 * Always acquire from `fighterLaserPool` (see `fighterDroneFire.ts`)
 * rather than `new`-ing directly outside of pool pre-warm.
 *
 * NO React imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module FighterDroneLaser
 */

import { BaseEntity } from './BaseEntity';
import type { LevelRuntime } from '../engine/gameState';
import { spaceState } from '../engine/spaceState';
import {
  FIGHTER_DRONE_LASER_WIDTH,
  FIGHTER_DRONE_LASER_HEIGHT,
  FIGHTER_DRONE_LASER_SPEED,
  FIGHTER_DRONE_LASER_OFFSCREEN_MARGIN,
} from '../../constants/spaceEnemyConstants';

/**
 * FighterDroneLaser — pooled left-travelling bolt fired by a
 * `FighterDrone`. Acquired by `fighterDroneFire` on each timer
 * crossing. Damage is applied by `shipDamage` on overlap.
 */
export class FighterDroneLaser extends BaseEntity {
  /** Monotonic ID counter for stable renderer keys across pool recycling. */
  private static nextId = 1;

  /** Unique spawn identifier — re-issued each `spawn()` call. */
  public spawnId = 0;

  /** True while the bolt is actively travelling. */
  public active = false;

  constructor() {
    super(0, 0, FIGHTER_DRONE_LASER_WIDTH, FIGHTER_DRONE_LASER_HEIGHT);
    this.dead = true;
  }

  /**
   * Initialise the bolt at a world position. Always travels left at
   * `FIGHTER_DRONE_LASER_SPEED`.
   *
   * @param x - Spawn X in world space (logical px, top-left of hitbox).
   * @param y - Spawn Y in world space (logical px, top-left of hitbox).
   */
  public spawn(x: number, y: number): void {
    this.spawnId = FighterDroneLaser.nextId++;
    this.x = x;
    this.y = y;
    this.vx = -FIGHTER_DRONE_LASER_SPEED;
    this.vy = 0;
    this.dead = false;
    this.active = true;
  }

  /** Reset state for pool recycling. Called by `ObjectPool.release()`. */
  public reset(): void {
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
    this.dead = true;
    this.active = false;
  }

  /**
   * Advance the bolt by one fixed timestep. Despawns when its RIGHT
   * edge crosses the LEFT cull line of the viewport (camera-relative,
   * mirror of `BossProjectile`).
   *
   * @param dt    - Fixed timestep in seconds.
   * @param level - Unused; kept for `BaseEntity.update` contract.
   */
  public update(dt: number, level: LevelRuntime): void {
    void level;
    if (!this.active) return;

    this.x += this.vx * dt;
    this.y += this.vy * dt;

    const leftCullX = spaceState.cameraX - FIGHTER_DRONE_LASER_OFFSCREEN_MARGIN;
    if (this.x + this.width < leftCullX) {
      this.dead = true;
      this.active = false;
    }
  }
}
